import React from 'react'
import styled, { keyframes } from 'styled-components'
import { skills } from '../../data/constants'

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 0;
`;

const Wrapper = styled.div`
  max-width: 1100px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 0 20px;
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 700;
  text-align: center;
  margin-top: 20px;
  background: ${({ theme }) => theme.gradient_text};
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  max-width: 600px;
  text-align: center;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SkillsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 30px;
  justify-content: center;
  gap: 30px;
`;

const Skill = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${({ theme }) => theme.glass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.glass_border};
  border-radius: 20px;
  padding: 24px 36px;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  animation: ${fadeInUp} 0.6s ease-out backwards;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ theme }) => theme.gradient_primary};
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${({ theme }) => theme.primary + '10'} 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px ${({ theme }) => theme.shadow_primary};
    border-color: ${({ theme }) => theme.primary + '50'};
  }

  &:hover::before {
    opacity: 1;
  }
  
  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 768px) {
    max-width: 400px;
    padding: 16px 24px;
  }

  @media (max-width: 550px) {
    max-width: 330px;
    padding: 14px 20px;
  }
`;

const SkillTitle = styled.h3`
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: ${({ theme }) => theme.gradient_primary};
    margin: 12px auto 0;
    border-radius: 2px;
  }
`;

const SkillList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const SkillItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 15px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.glass_border};
  background: ${({ theme }) => theme.glass};
  padding: 12px 18px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: default;

  &:hover {
    transform: translateY(-4px) scale(1.02);
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 10px 25px ${({ theme }) => theme.shadow_primary};
    background: ${({ theme }) => theme.primary + '15'};
    color: ${({ theme }) => theme.primary};
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 14px;
  }

  @media (max-width: 500px) {
    font-size: 13px;
    padding: 8px 12px;
  }
`;

const SkillImage = styled.img`
  width: 24px;
  height: 24px;
  transition: all 0.3s ease;
  
  ${SkillItem}:hover & {
    transform: scale(1.1);
  }
`;

const Skills = () => {
  return (
    <Container id='skills'>
      <Wrapper>
        <Title>Skills</Title>
        <Desc>Here are some of my skills on which I have been working on for the past 3 years.</Desc>
        <SkillsContainer>
          {skills.map((item, index) => (
            <Skill key={index} style={{ animationDelay: `${index * 0.1}s` }}>
              <SkillTitle>{item.title}</SkillTitle>
              <SkillList>
                {item.skills.map((skill, skillIndex) => (
                  <SkillItem key={skillIndex}>
                    <SkillImage src={skill.image} alt={skill.name} />
                    {skill.name}
                  </SkillItem>
                ))}
              </SkillList>
            </Skill>
          ))}
        </SkillsContainer>
      </Wrapper>
    </Container>
  );
};

export default Skills;